import { useState, useMemo } from 'react';
import { Search, Package, X, Plus } from 'lucide-react';
import { useFilter } from '../../lib/filter-context';
import { useWorkspace } from '../../lib/workspace';
import { createAssignment } from '../../lib/layout-api';

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  'assigned': { cls: 'bg-green-100 text-green-700', label: 'Stokta' },
  'missing': { cls: 'bg-red-100 text-red-700', label: 'Eksik' },
  'in-stock': { cls: 'bg-orange-100 text-orange-700', label: 'Atanmis' },
  'unassigned': { cls: 'bg-gray-100 text-gray-600', label: 'Yok' },
};

type ItemRow = ReturnType<typeof useFilter>['filteredItemsWithBins'][number];

function relativeDays(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return 'bugun';
  if (diff === 1) return '1g';
  if (diff < 30) return `${diff}g`;
  if (diff < 365) return `${Math.floor(diff / 30)}ay`;
  return `${Math.floor(diff / 365)}y`;
}

function formatDKK(amount: number): string {
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k`;
  return amount.toFixed(0);
}

export function ProductPanel() {
  const {
    productPanelOpen, setProductPanelOpen,
    filteredItemsWithBins, isFilterActive,
    selectedItemId, setSelectedItemId,
    salesMap,
  } = useFilter();
  const { openTab, replaceActiveTab, bottomPanel, setActiveTab } = useWorkspace();

  const [searchQuery, setSearchQuery] = useState('');
  const [assigning, setAssigning] = useState(false);

  // All hooks MUST be before any early return
  const items = useMemo(() => {
    if (searchQuery.length >= 2) {
      const q = searchQuery.toLowerCase();
      return filteredItemsWithBins.filter(i =>
        i.item_id.includes(searchQuery) ||
        i.description.toLowerCase().includes(q) ||
        i.brand.toLowerCase().includes(q));
    }
    return filteredItemsWithBins;
  }, [searchQuery, filteredItemsWithBins]);

  if (!productPanelOpen) return null;

  // Active bin from bottom panel (for "Ata" button)
  const activeBinTab = bottomPanel.tabs.find(t => t.id === bottomPanel.activeTabId && t.type === 'bin');
  const activeBinCode = activeBinTab?.params.binCode as string | undefined;
  const canAssign = !!selectedItemId && !!activeBinCode;

  async function handleAssign() {
    if (!selectedItemId || !activeBinCode) return;
    setAssigning(true);
    try {
      await createAssignment(activeBinCode, selectedItemId);
    } catch (err) { console.error(err); }
    finally { setAssigning(false); }
  }

  function handleRowClick(itemId: string) {
    setSelectedItemId(selectedItemId === itemId ? null : itemId);
  }

  function openBinTab(code: string, e: React.MouseEvent) {
    e.stopPropagation();
    const tab = { type: 'bin' as const, label: code, params: { binCode: code }, closable: true };
    if (e.ctrlKey || e.metaKey) {
      openTab('bottom', { ...tab, id: `bin-${code}-${Date.now()}` });
    } else {
      const existing = bottomPanel.tabs.find(t => t.type === 'bin' && t.params.binCode === code);
      if (existing) {
        setActiveTab('bottom', existing.id);
      } else {
        replaceActiveTab('bottom', tab);
      }
    }
  }

  function renderItemRow(item: ItemRow) {
    const isSelected = item.item_id === selectedItemId;
    const badge = STATUS_BADGE[item.status];
    const displayBins = item.assignedBins.length > 0 ? item.assignedBins : item.bins.map(b => b.bin_code);
    const sale = salesMap.get(item.item_id);
    return (
      <div key={item.item_id}
        onClick={() => handleRowClick(item.item_id)}
        className={`flex items-center gap-1 px-2 py-1 border-b cursor-pointer transition-colors text-[11px] ${
          isSelected ? 'bg-accent-light' : 'hover:bg-hover'
        }`}
        style={{ borderColor: 'rgba(33,35,39,0.04)' }}>
        <span className="font-mono text-accent shrink-0 w-[52px]">{item.item_id}</span>
        <div className="flex-1 min-w-0">
          <span className="text-primary truncate block">{item.description}</span>
          {item.brand && <span className="text-[9px] text-tertiary">{item.brand}</span>}
        </div>
        <div className="flex gap-0.5 shrink-0">
          {displayBins.slice(0, 2).map(b => (
            <span key={b} onClick={e => openBinTab(b, e)}
              className="px-1 py-0 rounded text-[8px] font-mono bg-surface text-accent cursor-pointer hover:bg-accent-light">
              {b}
            </span>
          ))}
          {displayBins.length > 2 && <span className="text-[8px] text-tertiary">+{displayBins.length - 2}</span>}
          {displayBins.length === 0 && <span className="text-[8px] text-tertiary">{'\u2014'}</span>}
        </div>
        <span className={`inline-flex items-center rounded-full px-1.5 py-0 text-[8px] font-semibold shrink-0 ${badge.cls}`}>
          {badge.label}
        </span>
        <span className="text-[9px] text-tertiary shrink-0 w-[24px] text-right">{relativeDays(item.last_sale)}</span>
        {sale && sale.sales_amount_30d > 0 && (
          <span className="text-[9px] text-secondary shrink-0 w-[36px] text-right">{formatDKK(sale.sales_amount_30d)}</span>
        )}
      </div>
    );
  }

  return (
    <div className="w-[360px] bg-white border-l flex flex-col shrink-0 overflow-hidden"
      style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-surface shrink-0"
        style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
        <div className="flex items-center gap-1.5">
          <Package size={13} className="text-accent" />
          <span className="text-[12px] font-semibold text-primary">Urunler</span>
          <span className="px-1.5 py-0.5 bg-accent text-white rounded-full text-[9px] font-semibold">
            {items.length}
          </span>
        </div>
        <button onClick={() => setProductPanelOpen(false)}
          className="p-1 text-tertiary hover:text-primary rounded-sm hover:bg-hover">
          <X size={14} />
        </button>
      </div>

      {/* Assign button */}
      {canAssign && (
        <div className="px-3 py-1.5 border-b bg-green-50 flex items-center gap-2 shrink-0"
          style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
          <button onClick={handleAssign} disabled={assigning}
            className="flex items-center gap-1.5 px-3 py-1 bg-accent text-white rounded-sm text-[11px] font-medium hover:bg-accent/90 disabled:opacity-50">
            <Plus size={12} />
            {selectedItemId} &rarr; {activeBinCode}
          </button>
          <span className="text-[10px] text-secondary">Ata</span>
        </div>
      )}

      {/* Search */}
      <div className="px-3 py-2 border-b shrink-0" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-tertiary" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Listede ara..."
            className="w-full pl-7 pr-2 py-1 text-[11px] border rounded-sm bg-white focus:outline-none focus:border-accent"
            style={{ borderColor: 'rgba(33,35,39,0.15)' }} />
        </div>
      </div>

      {/* Selected item indicator */}
      {selectedItemId && (
        <div className="px-3 py-1.5 border-b bg-accent-light/50 flex items-center justify-between shrink-0"
          style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
          <span className="text-[11px] text-accent font-medium">Secili: {selectedItemId}</span>
          <button onClick={() => setSelectedItemId(null)} className="text-[10px] text-accent hover:underline">Kaldir</button>
        </div>
      )}

      {/* Flat item list */}
      <div className="flex-1 overflow-auto min-h-0">
        {!isFilterActive && filteredItemsWithBins.length === 0 ? (
          <div className="text-center py-8 text-[12px] text-tertiary px-4">
            Sol panelden filtre uygulayarak urun listesini goruntuleyebilirsiniz.
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-[12px] text-tertiary">Sonuc yok</div>
        ) : (
          <div>
            {items.map(item => renderItemRow(item))}
          </div>
        )}
      </div>
    </div>
  );
}
